// src/encounter/wrap-up-problems.tsx
// M4b — the Wrap-Up "Problem list" closing step (PROD-1656 spec §10.5, PROD-1694; Samer-approved
// frames f8a–f8c; RATIFIED product behavior). A PEER of invoice/orders/sign that answers "does the
// problem list reflect today?" at a glance:
//   • JOINING THE LIST AT SIGN — the ruling-C auto-promotion made visible, with the one-tap
//     stewardship opt-out ("Don't add to problem list" — the SAME handler the A&P card fires);
//   • RESOLVED TODAY — with one-tap Reactivate (walks the resolve back, original onset kept);
//   • CHRONIC STILL DUE — structurally supported but DATA-GATED: the chronic/DUE columns land
//     with the PROD-1682/1684 backend (in flight, un-merged) — no fabricated chips until then.
// NEVER BLOCKING (Samer law): no checkbox, no gate on Sign & Lock — this section renders state +
// actions only and holds NO sign wiring at all.
import {
  WrapUpProblemsSection,
  WrapUpProblemsGroup,
  ClinicalTermRow,
} from '@scripsteam/scrips-signal-ds';

export interface WrapUpJoiningRow {
  dxId: string;
  term: string;
  code?: string;
  /** Receipt: already opted out (renders the neutral receipt state, action retired). */
  keptOff?: boolean;
  onKeepOff?: () => void;
}

export interface WrapUpResolvedRow {
  id: string;
  term: string;
  code?: string;
  /** e.g. "Jul 2026" — the resolved chip vocabulary (ClinicalTermRow `resolvedOn`). */
  resolvedOn?: string;
  onReactivate?: () => void;
}

export interface WrapUpProblemsProps {
  joining: WrapUpJoiningRow[];
  resolvedToday: WrapUpResolvedRow[];
}

export function WrapUpProblems({ joining, resolvedToday }: WrapUpProblemsProps) {
  const joiningLive = joining.filter((j) => !j.keptOff);
  const summary: string[] = [];
  if (joiningLive.length > 0)
    summary.push(
      `${joiningLive.length} ${joiningLive.length === 1 ? 'diagnosis' : 'diagnoses'} joining the list at sign`,
    );
  if (resolvedToday.length > 0) summary.push(`${resolvedToday.length} resolved today`);
  if (summary.length === 0) summary.push('Problem list reflects today — nothing joining, nothing resolved');

  return (
    // Uncontrolled: the DS section owns open/closed (starts collapsed). The empty-visit line lives in
    // the DS too — `emptyBody` is shown when the expanded body has no group to draw.
    <WrapUpProblemsSection
      summary={summary}
      emptyBody={
        joining.length === 0 && resolvedToday.length === 0
          ? 'Nothing changed the problem list this visit.'
          : undefined
      }
    >
      {joining.length > 0 && (
        <WrapUpProblemsGroup label="Joining the list at sign" count={joiningLive.length}>
          {joining.map((j) => (
            <ClinicalTermRow
              key={j.dxId}
              term={j.term}
              code={j.code}
              state="active"
              // keptOff rows read as receipts: the action retires, the row stays visible so the
              // clinician sees what was steered off the list (reversible from the A&P card).
              secondary={j.keptOff ? 'Not added to problem list' : undefined}
              actions={
                !j.keptOff && j.onKeepOff
                  ? [{ label: "Don't add to problem list", onClick: j.onKeepOff }]
                  : undefined
              }
            />
          ))}
        </WrapUpProblemsGroup>
      )}
      {resolvedToday.length > 0 && (
        <WrapUpProblemsGroup label="Resolved today" count={resolvedToday.length}>
          {resolvedToday.map((r) => (
            <ClinicalTermRow
              key={r.id}
              term={r.term}
              code={r.code}
              state="resolved"
              resolvedOn={r.resolvedOn}
              actions={r.onReactivate ? [{ label: 'Reactivate', onClick: r.onReactivate }] : undefined}
            />
          ))}
        </WrapUpProblemsGroup>
      )}
    </WrapUpProblemsSection>
  );
}
